import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { getImageUrl } from "../utility/imageHelper";
import { slugify } from "../utility/slugify";
import "./styles/ProductDetails.css";

import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.css';
import ProductSEO from "../seo/ProductSEO";
import config from "../config/config";
import generateBreadcrumbs from "../utility/generateBreadcrumbs";

import { useAnalytics } from "../context/AnalyticsProvider";
import SimilarProducts from "../components/SimilarProducts";



const ProductDetails = () => {
  const navigate = useNavigate();

  const { productId } = useParams();
  
  const { addToCart, cart } = useCart();
  const { products, fetchProducts } = useStore();
  const [product, setProduct] = useState(null); // ✅ Local state to track product
  const cartItem = product ? cart.find((item) => item.id === product.id) : null;
  const quantityInCart = cartItem ? cartItem.quantity : 0;

  const analytics = useAnalytics();





  useEffect(() => {
    const fetchData = async () => {
      await fetchProducts(); // 🔄 Fetch latest products
    };
    fetchData();
  }, [productId, fetchProducts]); // ✅ Make sure it re-fetches when navigating to another product
  
  useEffect(() => {
    const foundProduct = products.find((p) => p.id === parseInt(productId));
    setProduct(foundProduct);
  }, [products, productId]);


  // Detect duplicate product names
  const nameCounts = products.reduce((acc, p) => {
    acc[p.name] = (acc[p.name] || 0) + 1;
    return acc;
  }, {});

  const duplicateNames = new Set(
    Object.entries(nameCounts)
      .filter(([_, count]) => count > 1)
      .map(([name]) => name)
  );



  useEffect(() => {
    if (!product) return;

    analytics.track("view_item", {
      currency: "USD",
      value: product.price,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.price,
          category: product.category_name,
          stock_remaining: product.stock,
          index: product.index_position || null,
        }
      ]
    });

  }, [product?.id]);


  
  // ✅ Recalculate max stock status when product or cart changes
  useEffect(() => {
    if (product) {
      const availableStock = product.stock - quantityInCart;
      setMaxStockReached(availableStock <= 0);
    }
  }, [product, quantityInCart]);

  

  const [selectedImage, setSelectedImage] = useState(null);
  const [added, setAdded] = useState(false);
  const [maxStockReached, setMaxStockReached] = useState(false);
  const [quantity, setQuantity] = useState(1); // ✅ New state


  
  

  const [activeTab, setActiveTab] = useState("overview");

  const handleBuyNow = () => {
    if (!product || quantity + quantityInCart > product.stock) return;

    // 🔥 Fire analytics event
    analytics.track("add_to_cart", {
      currency: "USD",
      value: (product.price * quantity).toFixed(2),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.price,
          category: product.category_name,
          quantity: quantity,
          stock_remaining: product.stock - quantity,
          index: product.index_position || null,
        }
      ]
    });

    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    navigate("/secure-checkout");
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
  
    // Allow empty input temporarily for user editing
    if (value === "") {
      setQuantity("");
      return;
    }
  
    const num = Number(value);
  
    // Prevent non-numeric values
    if (isNaN(num)) return;
  
    // Enforce minimum of 1 and maximum of available stock
    if (num >= 1 && num <= product.stock - quantityInCart) {
      setQuantity(num);
    }
  };
  
  

  const increaseQuantity = () => {
    if (quantity + quantityInCart < product.stock) {
      setQuantity((prev) => prev + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  useEffect(() => {
    if (product?.images.length > 0) {
      setSelectedImage(product.images[0]);
    }
  }, [product]);

  useEffect(() => {
    if (product && quantityInCart >= product.stock) {
      setMaxStockReached(true);
    }
  }, [quantityInCart, product]);

  useEffect(() => {
    if (product) {
      const availableStock = product.stock - quantityInCart;
      if (quantity > availableStock) {
        setQuantity(availableStock > 0 ? availableStock : 1);
      }
    }
  }, [quantityInCart, product, quantity]);
  

  const handleAddToCart = () => {
    if (!product || quantity + quantityInCart > product.stock) return;

    // Fire GA4 add_to_cart event
    analytics.track("add_to_cart", {
      currency: "USD",
      value: (product.price * quantity).toFixed(2),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.price,
          category: product.category_name,
          quantity: quantity,
          stock_remaining: product.stock - quantity,
          index: product.index_position || null,
        }
      ]
    });


    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    setAdded(true);

    setTimeout(() => {
      setAdded(false);
      if (quantityInCart + quantity >= product.stock) {
        setMaxStockReached(true);
      }
    }, 4000);
  };

  if (!product) return <div className="loader-wrapper-global"><div className="spinner"></div></div>;

  const isOutOfStock = product.stock <= 0;

  const remainingStock = product.stock - quantityInCart;
  const onlyOneLeft = remainingStock === 1;

  // Prioritize Larry first, then Jason, then others
  const sortedTags = [...product.tags].sort((a, b) => {
    const priority = (tag) => {
      if (/larry withrow/i.test(tag)) return 1;
      if (/jason withrow/i.test(tag)) return 2;
      return 3; // everything else
    };
    return priority(a) - priority(b);
  });


  const makerTags = sortedTags.filter(tag => /withrow/i.test(tag));
  const specTags  = sortedTags.filter(tag => !/withrow/i.test(tag));



  return (
    <>
      <ProductSEO
          title={`${product.name} - Withrow Knives`}
          description={`${product.description} | Buy the ${product.name} now, handcrafted for quality and durability. Each knife comes with a custom leather sheath.`}
          keywords={[
              "custom knives",
              "handmade knives",
              product.name.toLowerCase(),
              ...product.tags,
              "Withrow Knives"
          ]}
          url={`${config.metadata.siteUrl}/shop/products/${product.id}/${slugify(product.name)}`}
          image={getImageUrl(product.images?.[0])}
          price={product.price}
          availability={product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"}
          jsonLd={{
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "image": product.images.map(img => getImageUrl(img)),
              "description": product.description,
              "brand": {
                  "@type": "Brand",
                  "name": "Withrow Knives"
              },
              "sku": product.id,
              "mpn":product.id,
              "offers": {
                  "@type": "Offer",
                  "url": `${config.metadata.siteUrl}/shop/products/${product.id}/${slugify(product.name)}`,
                  "priceCurrency": "USD",
                  "price": product.price,
                  "itemCondition": "https://schema.org/NewCondition",
                  "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                  "seller": {
                      "@type": "Store",
                      "name": "Withrow Knives"
                  }
              },
              "breadcrumb": generateBreadcrumbs(["shop-knives", "knife"], product),
              "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `${config.metadata.siteUrl}/shop-knives/knife/${product.id}/${slugify(product.name)}`
              },

          }}
      />



      <div className="product-details-wrapper">

      
      <div className="product-details-container">
        {/* ✅ Left Section */}
        <div className="product-left">
          <div className="product-images">
            <div className="image-zoom-wrapper">
              {selectedImage && (
                <InnerImageZoom
                src={getImageUrl(selectedImage)}
                zoomSrc={getImageUrl(selectedImage)}
                alt={product.name}
                className="main-image"
                zoomType="click"
                zoomScale={2}
                zoomPreload={true}
                hideHint={true}
                hasSpacer={true}
                zoomPosition="original"
                moveType="pan"
              />
              )}
            </div>
            <div className="thumbnail-container">
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={getImageUrl(img)}
                  alt={`Thumbnail ${index + 1} of ${product.name}`}
                  className={`thumbnail ${selectedImage === img ? "active" : ""}`}
                  onClick={() => setSelectedImage(img)}
                  tabIndex="0" // ✅ Makes it focusable for keyboard users
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); // Prevents scrolling when pressing Space
                      setSelectedImage(img);
                    }
                  }}
                  aria-label={`Select image ${index + 1} of ${product.name}`} // ✅ Improves screen reader announcement
                />
              ))}
            </div>

          </div>
        </div>

        {/* ✅ Right Section */}
        <div className="product-info" aria-labelledby="product-info-title">
        <h2 id="product-info-title" className="sr-only">Product Information</h2>
          <div className="product-info-content">
            {product.category_name && (
              <Link
                to={`/shop-knives/category/${product.category_name.toLowerCase().replace(/\s+/g, "-")}`}
                className="product-category-link"
                aria-label={`View more products in category: ${product.category_name}`}
              >
                <i className="fa-solid fa-list"></i>&nbsp;{product.category_name}
              </Link>
            )}


            <h1>
              {product.name}
              {duplicateNames.has(product.name) && (
                <> <span className="product-number">(#{product.id})</span></>
              )}
            </h1>
            {makerTags.length > 0 && (
              <p className="maker-tag-line">
                {makerTags.join(", ")}
              </p>
            )}



            <p className="product-description">{product.description}</p>

            {/* Category + Specs / Tags */}
            <div className="product-meta">
 
              <h4 className="spec-title">Specifications</h4>
              {product.tags.length > 0 && (
                <ul className="product-specs">
                  {specTags.map((tag, index) => (
                    <li key={index}>{tag}</li>
                  ))}

                  {product.sheath_included === 1 && (
                    <li><strong>Hand-sewn leather sheath included</strong></li>
                  )}
                </ul>
              )}
            </div>


              
            <p className="product-price">${product.price}</p>
            
            {quantityInCart > 0 && (
              <div className="in-cart-message">
            
              <p>
                This item is in your cart.
                {quantityInCart > 1 && (
                  <> <strong>({quantityInCart}x)</strong></>
                )}
              </p>
              <Link to="/shopping-cart">Go to cart</Link>
              </div>
            )}


            <div className="product-info-row">
              {/* ✅ Quantity Selector with Input */}
              <div className="quantity-selector-wrapper">


                
                 &nbsp;
                
                <div className="quantity-selector">
                  

                  <button
                    onClick={decreaseQuantity}
                    aria-label="Decrease quantity"
                    disabled={
                      isOutOfStock ||
                      maxStockReached ||
                      onlyOneLeft ||          // Disable if there's only one
                      quantity <= 1
                    }
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>

                  <input
                    type="number"
                    className="quantity-input"
                    value={maxStockReached ? 0 : quantity}
                    onChange={handleQuantityChange}
                    aria-label="Enter quantity"
                    disabled={
                      isOutOfStock ||
                      maxStockReached ||
                      onlyOneLeft            // Disable manual edits if just 1 in stock
                    }
                  />

                  <button
                    onClick={increaseQuantity}
                    aria-label="Increase quantity"
                    disabled={
                      isOutOfStock ||
                      maxStockReached ||
                      quantity + quantityInCart >= product.stock ||
                      onlyOneLeft            // Disable if just 1 left
                    }
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>

                </div>


                <p className="stock-note" aria-live="polite">
                  {product.stock - quantityInCart === 1
                    ? "Only 1 available."
                    : `${product.stock - quantityInCart} available`}
                </p>

              </div>


            




              <hr />

              <div className="product-buttons">
                {isOutOfStock ? (
                  <button className="sold-out-button" disabled>
                    <i className="fa-solid fa-box-open"></i> Sold Out
                  </button>
                ) : maxStockReached ? (
                  <button className="sold-out-button" disabled>
                    <i className="fa-solid fa-check"></i> Max Stock Added
                  </button>
                ) : (
                  <>
                    <button
                      className="add-to-cart-button"
                      onClick={handleAddToCart}
                      disabled={added}
                    >
                      {added ? (
                        <>
                          <i className="fa-solid fa-check"></i> Added to Cart
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-cart-plus"></i> Add to Cart
                        </>
                      )}
                    </button>
                    <span>or</span>
                    <button
                      className="buy-now-button"
                      onClick={handleBuyNow}
                    >
                      Buy It Now <i className="fa-regular fa-circle-right"></i>
                    </button>
                  </>
                )}
              </div>
            </div>

          </div>

        </div>
      </div>
      {/* ✅ Tab Navigation */}
      <div className="product-description-tabs">
        <div className="tabs" role="tablist" aria-label="Product Details Tabs">
          <button
            role="tab"
            id="tab-overview"
            className={activeTab === "overview" ? "active" : ""}
            onClick={() => setActiveTab("overview")}
            aria-selected={activeTab === "overview"}
            aria-controls="tab-content-overview"
          >
            Overview
          </button>

          <button
            role="tab"
            id="tab-description"
            className={activeTab === "description" ? "active" : ""}
            onClick={() => setActiveTab("description")}
            aria-selected={activeTab === "description"}
            aria-controls="tab-content-description"
          >
            Description
          </button>
          <button
            role="tab"
            id="tab-warranty"
            className={activeTab === "warranty" ? "active" : ""}
            onClick={() => setActiveTab("warranty")}
            aria-selected={activeTab === "warranty"}
            aria-controls="tab-content-warranty"
          >
            Warranty & Returns
          </button>
          <button
            role="tab"
            id="tab-shipping"
            className={activeTab === "shipping" ? "active" : ""}
            onClick={() => setActiveTab("shipping")}
            aria-selected={activeTab === "shipping"}
            aria-controls="tab-content-shipping"
          >
            Shipping
          </button>
        </div>

        {/* ✅ Tab Content */}
        <div className="tab-content">
          {activeTab === "overview" && (
            <div id="tab-content-overview" role="tabpanel" aria-labelledby="tab-overview">

              {/* 👉 Description Section */}
              <section className="overview-section">
                <h3>Description</h3>
                <p>{product.description || "No additional description available."}</p>

                {product.tags.length > 0 && (
                  <>
                    <h4>This Knife is equipped with:</h4>
                    <ul>
                      {specTags.map((tag, index) => (
                        <li key={index}>{tag}</li>
                      ))}

                      {product.sheath_included === 1 && (
                        <li><strong>Hand-sewn leather sheath included</strong></li>
                      )}
                    </ul>
                  </>
                )}


                <p>If you have further questions, <Link to={"/contact"}>contact us here</Link>.</p>
              </section>

              <hr />

              {/* 👉 Warranty Section */}
              <section className="overview-section">
                <h3>Warranty & Returns</h3>
                <p>
                  We do not offer a strict warranty or return policy, but if you feel something is wrong with your item, we would love to hear about it and work something out. 
                  <Link to={"/contact"}> contact us here</Link>.
                </p>
              </section>

              <hr />

              {/* 👉 Shipping Section */}
              <section className="overview-section">
                <h3>Shipping</h3>
                <p>
                  Shipping is handled through one of the major mail carriers, and can vary on the amount of time it takes to process. 
                  We will ship your item(s) ASAP, but it may take slightly longer than usual depending on your order and on the carrier.
                  If your order contains an expensive item or items, we highly recommend purchasing shipping insurance at checkout.
                </p>
              </section>

            </div>
          )}

          {activeTab === "description" && (
            <div id="tab-content-description" role="tabpanel" aria-labelledby="tab-description">
              <p>{product.description || "No additional description available."}</p>
              {product.tags.length > 0 && (
                <>
                  <h4>This Knife is equipped with:</h4>
                  <ul>
                    {specTags.map((tag, index) => (
                      <li key={index}>{tag}</li>
                    ))}
                  </ul>
                </>
              )}
              <p>If you have further questions, <Link to={"/contact"}>contact us here</Link>.</p>
            </div>
          )}

          {activeTab === "warranty" && (
            <div id="tab-content-warranty" role="tabpanel" aria-labelledby="tab-warranty">
              <p>
                We do not offer a strict warranty or return policy, but if you feel something is wrong with your item, we would love to hear about it and work something out. <Link to={"/contact"}>contact us here</Link>.
              </p>
            </div>
          )}

          {activeTab === "shipping" && (
            <div id="tab-content-shipping" role="tabpanel" aria-labelledby="tab-shipping">
              <p>
                Shipping is handled through one of the major mail carriers, and can vary on the amount of time it takes to process. We will ship your item(s) ASAP, but it may take slightly longer than usual depending on your order and on the carrier.
                If your order contains an expensive item or items, we recommend purchasing shipping insurance at checkout.
              </p>
            </div>
          )}
        </div>
      </div>


      <SimilarProducts product={product} />


      </div>
    </>
  );
};

export default ProductDetails;
